import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "chat-body"
}
const _hoisted_2 = ["textContent"]
const _hoisted_3 = { class: "container-summary" }
const _hoisted_4 = { class: "badge-container container-item" }
const _hoisted_5 = { class: "memo-header" }
const _hoisted_6 = { class: "actions-wrapper" }
const _hoisted_7 = { class: "editor-container container-item" }

import { ref, inject, onMounted } from "vue";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import moment from "moment";
import { formatSummaryData } from "@/utils/formatters";
import { UserTracking } from "@/utils/UserTracking";
import { trackNoaRumAction, NOA_RUM_EVENT } from "@/utils/noaObservability";

interface ApiClient {
  get: (url: string, params?: any) => Promise<any>;
  post: (url: string, data?: any) => Promise<any>;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SummaryContent',
  props: {
  actionState: {
    type: String,
    required: true,
  },
  reviewStatus: {
    type: String,
    required: true,
  },
  summaryText: {
    type: String,
    required: true,
  },
  sessionTimestamp: {
    type: String,
    required: true,
  },
  initialContext: {
    type: Object,
    required: true,
  },
},
  emits: ["update:summaryText"],
  setup(__props, { emit: __emit }) {

const apiClient = inject<ApiClient | null>("apiClient", null);

const props = __props;

const quillEditor = ref(null);
const toastsData = ref([]);
const isRegenerating = ref(false);
let editDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const emit = __emit;

const today = new Date().toISOString().split("T")[0];

onMounted(() => {
  if (props.summaryText && props.summaryText.trim().length > 0) {
    UserTracking.trackEvent("noa_notes_summary_opened", {
      session_id: props.initialContext?.recorderSessionId ?? "unknown",
      review_status: props.reviewStatus,
      page_category: "noa notes",
      page_url: window.location.href,
    });
    trackNoaRumAction(NOA_RUM_EVENT.SUMMARY_VIEWED, {
      session_id: props.initialContext?.recorderSessionId ?? "unknown",
      review_status: props.reviewStatus,
    });
  }
});

const quillOptions = {
  modules: {
    toolbar: ["bold", "italic", "underline", { list: "ordered" }, { list: "bullet" }],
  },
  placeholder: "Digite...",
  theme: "snow",
};

const handleRegenerateSummary = async () => {
  const sessionId = props.initialContext?.recorderSessionId;
  if (!apiClient || !sessionId) return;

  isRegenerating.value = true;
  try {
    const response = await apiClient.get(`/session/${sessionId}/regenerate-summary`);
    const formattedSummary = formatSummaryData(JSON.parse(response.content).result);
    emit("update:summaryText", formattedSummary);
  } catch (err) {
    console.error("Failed to regenerate summary:", err);
    toastsData.value = [{ content: "Erro ao regenerar resumo.", variant: "error", timeOut: 3000 }];
  } finally {
    isRegenerating.value = false;
  }
};

const handleCopyClick = async () => {
  try {
    const textToCopy = quillEditor?.value?.getText();

    await navigator.clipboard.writeText(textToCopy);
    toastsData.value = [];

    const newToast = { content: "Texto copiado com sucesso!", variant: "default", timeOut: 3000 };

    toastsData.value.push(newToast);

    UserTracking.trackEvent("summary_copied", {
      action: "mouse click copy",
      session_id: props.initialContext?.recorderSessionId ?? "unknown",
      page_category: "noa notes",
      page_section: "",
      page_url: window.location.href,
    });
  } catch (err) {
    console.error("Failed to copy text:", err);
  }
};

const handleKeyDown = (e) => {
  // Ctrl+C / Cmd+C → copy
  if ((e.ctrlKey || e.metaKey) && e.key === "c") {
    UserTracking.trackEvent("summary_copied", {
      action: "keyboard copy",
      session_id: props.initialContext?.recorderSessionId ?? "unknown",
      page_category: "noa notes",
      page_section: "",
      page_url: window.location.href,
    });
    return;
  }

  // Detect actual text-modifying keys, ignore navigation/modifier-only events
  const isEditKey = e.key.length === 1 || ["Backspace", "Delete", "Enter"].includes(e.key);
  if (!isEditKey) return;

  if (editDebounceTimer) clearTimeout(editDebounceTimer);
  editDebounceTimer = setTimeout(() => {
    UserTracking.trackEvent("noa_notes_summary_edited", {
      session_id: props.initialContext?.recorderSessionId ?? "unknown",
      page_category: "noa notes",
      page_url: window.location.href,
    });
    trackNoaRumAction(NOA_RUM_EVENT.SUMMARY_EDITED, {
      session_id: props.initialContext?.recorderSessionId ?? "unknown",
    });
  }, 1000);
};

return (_ctx: any,_cache: any) => {
  const _component_w_banner = _resolveComponent("w-banner")!
  const _component_w_button = _resolveComponent("w-button")!
  const _component_w_toast = _resolveComponent("w-toast")!

  return (__props.actionState === 'summary')
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (__props.sessionTimestamp !== _unref(today) && __props.reviewStatus === 'pending')
          ? (_openBlock(), _createBlock(_component_w_banner, {
              key: 0,
              dismissible: false,
              variant: "warning",
              class: "warning-banner"
            }, {
              default: _withCtx(() => [
                _cache[1] || (_cache[1] = _createElementVNode("strong", null, "Atenção!", -1)),
                _cache[2] || (_cache[2] = _createTextVNode(" Existe um resumo de ", -1)),
                _createElementVNode("span", {
                  textContent: _toDisplayString(_unref(moment)(__props.sessionTimestamp).format('DD/MM/YYYY'))
                }, null, 8, _hoisted_2),
                _cache[3] || (_cache[3] = _createTextVNode(" deste paciente que não foi salvo. Deseja continuar de onde parou? ", -1))
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_3, [
          _cache[4] || (_cache[4] = _createElementVNode("p", null, " Seu resumo automático está pronto. Ao utilizar estas anotações, você confirma para nós, que ele está preciso e representa o que ocorreu durante a consulta. ", -1)),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_component_w_button, {
                  variant: "plain",
                  label: "Regerar resumo",
                  "leading-icon": "repeat",
                  disabled: isRegenerating.value,
                  loading: isRegenerating.value,
                  class: "regenerate-button",
                  onClick: handleRegenerateSummary
                }, null, 8, ["disabled", "loading"]),
                _createVNode(_component_w_button, {
                  variant: "plain",
                  label: "Copiar",
                  "leading-icon": "clipboard",
                  onClick: handleCopyClick,
                  class: "copy-button"
                })
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_unref(QuillEditor), {
              ref_key: "quillEditor",
              ref: quillEditor,
              theme: "snow",
              content: __props.summaryText,
              contentType: "html",
              options: quillOptions,
              "onUpdate:content": _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('update:summaryText', $event))),
              onKeydown: handleKeyDown
            }, null, 8, ["content"])
          ])
        ]),
        _createVNode(_component_w_toast, { toasts: toastsData.value }, null, 8, ["toasts"])
      ]))
    : _createCommentVNode("", true)
}
}

})